import React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { formatDate } from '../../utils/dateUtils';

// Styled components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
`;

const Hero = styled.section`
  text-align: center;
  padding: 80px 0 60px;
  color: white;
  
  @media (max-width: 768px) {
    padding: 60px 0 40px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  font-weight: 300;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Content = styled.div`
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 60px 40px;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.1);
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    border-radius: 20px 20px 0 0;
  }
`;

const Section = styled.section`
  margin-bottom: 50px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #2d3748;
  margin-bottom: 30px;
  font-weight: 600;
  position: relative;
  padding-bottom: 15px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 25px;
  text-align: justify;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const FeatureCard = styled.div`
  background: #f7fafc;
  padding: 30px;
  border-radius: 15px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    border-color: #667eea;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 15px;
  text-align: center;
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 15px;
  text-align: center;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #718096;
  text-align: center;
`;

const ContactInfo = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 40px;
  border-radius: 15px;
  text-align: center;
  margin-top: 40px;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 20px;
    font-weight: 600;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 15px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  a {
    color: #ffd700;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const AboutPage: React.FC = () => {
  const currentDate = formatDate(new Date().toISOString());

  return (
    <PageContainer>
      <Helmet>
        <title>About Us - Infonium | Modern Information Platform</title>
        <meta name="description" content="Learn about Infonium - Your modern information platform delivering high-quality news and content with optimal user experience for the digital world." />
        <meta name="keywords" content="about infonium, introduction, mission, vision, information platform, quality news, media platform" />
        <meta name="author" content="Infonium" />
        <meta property="og:title" content="About Us - Infonium" />
        <meta property="og:description" content="Learn about Infonium - modern information platform with optimal user experience." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/about'} />
        <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta property="og:site_name" content="Infonium" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Us - Infonium" />
        <meta name="twitter:description" content="Learn about Infonium - modern information platform with optimal user experience." />
        <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta name="twitter:site" content="@infonium" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/about'} />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en" />
        <meta name="revisit-after" content="7 days" />
      </Helmet>
      <ContentWrapper>
        <Hero>
          <HeroTitle>About Infonium</HeroTitle>
          <HeroSubtitle>Modern Information Platform for the Digital World</HeroSubtitle>
        </Hero>

        <Content>
          <Section>
            <SectionTitle>Our Mission</SectionTitle>
            <Paragraph>
              Infonium was founded with the mission to deliver high-quality, accurate, and timely 
              information to users worldwide. We believe that information is power, and everyone 
              deserves access to valuable content presented in an understandable and engaging way.
            </Paragraph>
            <Paragraph>
              With our team of professional editors and modern technology, we are committed to 
              providing the best news reading experience, from user-friendly interfaces to 
              diverse and rich content.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Key Features</SectionTitle>
            <FeatureGrid>
              <FeatureCard>
                <FeatureIcon>📱</FeatureIcon>
                <FeatureTitle>Mobile Optimized</FeatureTitle>
                <FeatureDescription>
                  Modern responsive interface optimized for all devices, from smartphones to desktops
                </FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon>🎧</FeatureIcon>
                <FeatureTitle>Multimedia Content</FeatureTitle>
                <FeatureDescription>
                  Support for audio content and subtitles for an enhanced multimedia experience
                </FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon>⚡</FeatureIcon>
                <FeatureTitle>High Performance</FeatureTitle>
                <FeatureDescription>
                  Performance optimization with smart caching and global CDN for fast loading
                </FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon>🔒</FeatureIcon>
                <FeatureTitle>Security</FeatureTitle>
                <FeatureDescription>
                  Ensuring user information security with high-standard security protocols
                </FeatureDescription>
              </FeatureCard>
            </FeatureGrid>
          </Section>

          <Section>
            <SectionTitle>Quality Commitment</SectionTitle>
            <Paragraph>
              We are committed to maintaining the highest standards of content quality, information 
              accuracy, and user experience. Every article is carefully reviewed before publication.
            </Paragraph>
            <Paragraph>
              Infonium strictly adheres to copyright regulations, privacy rights, and journalistic 
              ethics. We respect user rights and are committed to protecting personal information 
              according to international standards.
            </Paragraph>
          </Section>

          <ContactInfo>
            <h3>Contact Us</h3>
            <p>If you have any questions about Infonium, please don't hesitate to reach out:</p>
            <p><strong>Email:</strong> <a href="mailto:contact@infonium.org">contact@infonium.org</a></p>
            <p><strong>Website:</strong> <a href="https://www.infonium.org">www.infonium.org</a></p>
            <p>We're always here to listen and provide quick responses to your inquiries.</p>
          </ContactInfo>
        </Content>
      </ContentWrapper>
    </PageContainer>
  );
};

export default AboutPage; 